import { useMemo } from "react";
import { UIFlightData } from "@flights/types";
import useRouteName from "../../../hooks/useRouteName";
import { useSelector } from "react-redux";
import { getAncillaries, getFlightDetailsFetchStatus } from "../../../store/flightDetails/selectors";
import { useI18n, t } from "@bookingcom/lingojs-react";
import {
  priceBreakdownUtils,
  localizationUtils,
  isFeatureRunningClientSide,
  ryanairUtils
} from "@flights/web-api-utils-universal";
import usePointOfSale from "../../../hooks/usePointOfSale";
import { getExtraProducts, getPreOptedAncillaries } from "../../../ancillaries/store/extraProducts/selectors";
import { transformPreOptedExtras } from "./transforms/preOptedExtras";
import uniq from "lodash/uniq";
import { transformDisclaimersForExtras, transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import { type BreakdownProps } from "./Breakdown/Breakdown";
import useEvents from "../../../hooks/useEvents";
import isEmpty from "lodash/isEmpty";

export type UnifiedPriceBreakdownFlightCardPriceControllerProps = {
  flight?: UIFlightData;
};

type CopyFuncParams = Parameters<typeof priceBreakdownUtils.getExtraCopy>;
type POSType = CopyFuncParams[3]["pointOfSale"];

export function useUnifiedPriceBreakdownFlightCardPriceController(
  props: UnifiedPriceBreakdownFlightCardPriceControllerProps
) {
  const { flight } = props;
  const i18n = useI18n();
  const ipCountry = usePointOfSale();
  const routeName = useRouteName();
  const { squeak } = useEvents();
  const ancillaries = useSelector(getAncillaries);
  const extraProducts = useSelector(getExtraProducts);
  const preOptedAncillaries = useSelector(getPreOptedAncillaries);
  const fetchStatus = useSelector(getFlightDetailsFetchStatus);
  const segments = useMemo(() => flight?.segments, [flight]);
  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );
  const baseOptions = useMemo(
    () => ({
      pointOfSale: (ipCountry || "") as POSType,
      isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
      segments: segments || []
    }),
    [ipCountry, isHungaryDepartingRyanAir, segments]
  );
  const unifiedPriceBreakdown = useMemo(() => {
    const upb = flight?.unifiedPriceBreakdown;
    if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
      squeak(
        `UnifiedPriceTag::useUnifiedPriceBreakdownFlightCardPriceController - product id is missing - route "${routeName}"`
      );
    }

    return upb;
  }, [flight, routeName, squeak]);

  const allAncillaries = useMemo(() => {
    return !isEmpty(extraProducts) ? extraProducts : !isEmpty(ancillaries) ? ancillaries : flight?.ancillaries || {};
  }, [flight, ancillaries, extraProducts]);

  const preOptedExtras = useMemo(
    () => transformPreOptedExtras(preOptedAncillaries, allAncillaries),
    [preOptedAncillaries, allAncillaries]
  );

  const addedItems = useMemo(() => {
    // show pre-opted ancillaries only in flight details
    if (!routeName.includes("flightdetails")) return [];

    return priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(i18n, preOptedExtras, baseOptions);
  }, [routeName, i18n, baseOptions, preOptedExtras]);

  const isFlightDetailsLoading = useMemo(() => {
    if (!routeName.includes("flightdetails")) return false;
    return fetchStatus === "loading";
  }, [fetchStatus, routeName]);

  const flightDetailsLoadingText = useMemo(() => {
    return i18n.trans(t("flights_price_breakdown_loading_details"));
  }, [i18n]);

  const disclaimers = useMemo(() => {
    return uniq([
      ...transformDisclaimersForPriceBreakdown(flight?.priceBreakdown),
      ...transformDisclaimersForExtras(preOptedExtras)
    ]);
  }, [flight, preOptedExtras]);

  const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    isFlightDetailsLoading,
    expandedByDefault,
    disclaimers,
    flightDetailsLoadingText,
    addedItems
  };
}
